import type { HttpInterceptorFn } from "@angular/common/http";
import { HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { HotToastService } from "@ngxpert/hot-toast";
import { isDefined } from "@recursyve/nice-ts-utils";
import { catchError, lastValueFrom, throwError } from "rxjs";

import { AppService } from "../../store/app.service";

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
    const appService = inject(AppService);
    const router = inject(Router);
    const toastService = inject(HotToastService);

    return next(request).pipe(
        catchError((error: unknown) => {
            const normalizedError = normalizeError(error, request.url, appService, router, toastService);
            if (isDefined(normalizedError)) {
                return throwError(() => normalizedError);
            }

            return throwError(() => error);
        })
    );
};

function normalizeError(
    error: unknown,
    api: string,
    appService: AppService,
    router: Router,
    toastService: HotToastService
): unknown {
    if (error instanceof Error) {
        return error;
    }

    if (error instanceof HttpErrorResponse) {
        return handleHttpError(error, api, appService, router, toastService);
    }

    return error;
}

function handleHttpError(
    error: HttpErrorResponse,
    api: string,
    appService: AppService,
    router: Router,
    toastService: HotToastService
): HttpErrorResponse | null {
    if (error.status === 401) {
        void lastValueFrom(appService.logout());
        void router.navigate(["/login"]);
        return null;
    } else if (error.status === 403) {
        void router.navigate(["/dashboard/forbidden"]);
        toastService.warning("403");
        return null;
    }

    if (error.status === 404) {
        return error;
    }

    return error;
}
